<template>
  <f7-page name="campaign-detail">
    <f7-navbar :title="campaign?.name || '...'" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="!campaign" class="loading-block">
          <f7-preloader />
        </f7-block>

        <template v-else>
          <div class="campaign-hero">
            <div class="banner-wrap">
              <img
                v-if="campaign.banner"
                :src="campaign.banner"
                class="banner-img"
                :alt="$t('campaignDetail.campaignBanner')"
              />
              <div v-else class="banner-placeholder">
                <f7-icon f7="photo_fill" size="28" />
              </div>
              <f7-link
                v-if="canManage"
                class="edit-overlay banner-edit"
                :aria-label="$t('campaignDetail.campaignBanner')"
                @click="pickImage('banner')"
              >
                <f7-icon f7="pencil" size="13" />
                <span>{{ $t("common.banner") }}</span>
              </f7-link>
            </div>

            <div class="hero-body">
              <div class="logo-wrap">
                <img
                  v-if="campaign.logo"
                  :src="campaign.logo"
                  class="campaign-logo"
                  :alt="$t('campaignDetail.campaignLogo')"
                />
                <div v-else class="logo-placeholder">{{ initial }}</div>
                <f7-link
                  v-if="canManage"
                  class="edit-overlay logo-edit"
                  :aria-label="$t('campaignDetail.campaignLogo')"
                  @click="pickImage('logo')"
                >
                  <f7-icon f7="pencil" size="12" />
                </f7-link>
              </div>
              <div class="hero-text">
                <div class="hero-name">{{ campaign.name }}</div>
                <div class="type-status" :class="campaign.status.toLowerCase()">
                  {{ statusLabel(campaign.status) }}
                </div>
</div>
        </div>
      </div>

      <CampaignLifecycleBar
        v-if="campaign"
        :status="campaign.status"
        :can-manage="canManage"
      />

      <input
            ref="imageInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onImagePick"
          />

          <f7-block v-if="campaign.status === 'DRAFT' && canManage" class="draft-banner">
            <div class="draft-banner-text">
              <f7-icon f7="rocket_fill" size="18" />
              <div>
                <div class="draft-banner-title">{{ $t("campaign.launchHint") }}</div>
                <div class="draft-banner-sub">{{ $t("campaign.launchHintSub") }}</div>
              </div>
            </div>
            <f7-button
              large
              fill
              round
              class="draft-banner-btn"
              :loading="launching"
              @click="launchCampaign"
            >
              {{ $t("campaign.launchCampaign") }}
            </f7-button>
          </f7-block>

          <CampaignSetupChecklist
            v-if="canManage && !isClosed"
            :group-id="groupId"
            :campaign="campaign"
            :can-manage="canManage"
            :members-count="membersStore.members.length"
            @launch="launchCampaign"
            @banner="pickImage('banner')"
            @logo="pickImage('logo')"
          />

          <f7-block class="type-header">
            <f7-icon :f7="metaFor(campaign.type).icon" size="22" />
            <div>
              <div class="type-name">{{ metaFor(campaign.type).label }}</div>
              <div class="type-status" :class="campaign.status.toLowerCase()">
                {{ statusLabel(campaign.status) }}
              </div>
            </div>
          </f7-block>

          <CampaignProgressBar
            v-if="
              campaign.targetAmountCfa ||
              campaign.targetQuantity ||
              campaign.maxParticipants
            "
            :campaign="campaign"
          />

          <f7-block v-if="campaign.description">
            <p class="description-text" v-html="campaign.description"></p>
          </f7-block>

          <template v-if="supportsContributions && categoryAmounts.length">
            <f7-block-title>{{ $t("category.amountsPerCategory") }}</f7-block-title>
            <f7-block>
              <f7-list inset strong>
                <f7-list-item
                  v-for="(ca, i) in categoryAmounts"
                  :key="i"
                  :title="categoryDisplay(ca)"
                  :after="fmtAmount(ca.amount)"
                />
                <f7-list-item
                  v-if="lockCategoryAmounts"
                  :title="$t('category.lockAmounts')"
                  :after="$t('common.on')"
                />
              </f7-list>
            </f7-block>
          </template>

          <ContributionForm
            v-if="supportsContributions && !isClosed"
            :campaign-id="campaignId"
            :group-id="groupId"
            :campaign-name="campaign?.name || ''"
            :campaign-type="campaign?.type || 'FUND_COLLECT'"
            :category-amounts="categoryAmounts"
            :lock-category-amounts="lockCategoryAmounts"
            :free-contribution-text="freeContributionText"
            :f7route="f7route"
            :f7router="f7router"
            @contributed="reload"
          />

          <template v-if="supportsContributions && canManage">
            <ContributionSettingsEditor
              :campaign-id="campaignId"
              :category-amounts="categoryAmounts"
              :lock-category-amounts="lockCategoryAmounts"
              :free-contribution-text="freeContributionText"
              @saved="onSettingsSaved"
            />
          </template>

          <ParticipantRegisterForm
            v-if="supportsParticipants && !isClosed"
            :campaign-id="campaignId"
            :campaign-type="campaign.type"
            @registered="reload"
          />

          <template v-if="supportsItems">
            <f7-block-title>{{ $t("common.products") }}</f7-block-title>
            <f7-block>
              <ItemTierCard
                v-for="item in items"
                :key="item.id"
                :item="item"
                @committed="reload"
              />
            </f7-block>
          </template>

          <CycleTracker v-if="supportsCycles" :campaign-id="campaignId" />

          <PayoutKycWarning
            v-if="supportsPayouts"
            :group-id="groupId"
            :campaign="campaign"
          />

          <PayoutSection
            v-if="supportsPayouts"
            :group-id="groupId"
            :campaign-id="campaignId"
            :members="membersStore.members"
            :admin-count="adminCount"
            :viewer-id="keycloakStore.subject"
            :can-manage="canManage"
            :closed="isClosed"
            :check-auth="checkAuth"
          />

          <BidPanel
            v-if="supportsBids"
            :campaign-id="campaignId"
            :reserve-price="Number(campaign.config?.reservePriceCfa ?? 0)"
            :bid-increment="Number(campaign.config?.bidIncrementCfa ?? 1000)"
            @bid-placed="reload"
          />

          <f7-block v-if="campaign.type === 'TONTINE'">
            <f7-button fill large @click="openTontine">
              {{ $t("tontine.openTontine") }}
            </f7-button>
            <p class="text-color-gray tontine-entry-note">{{ $t("tontine.detailEntryHint") }}</p>
          </f7-block>

          <f7-block v-if="isClosed" class="closed-banner">
            <f7-icon f7="checkmark_seal_fill" size="16" />
            {{
              $t("campaign.thisCampaignIs", {
                status: statusLabel(campaign.status).toLowerCase(),
              })
            }}.
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import client from "../js/api/client.js";
import { metaFor, categoryMeta } from "../constants/campaign-capabilities.js";
import { useCampaign } from "../composables/useCampaign.js";
import { useCampaignsStore } from "../js/pinia-stores/campaign.js";
import { useMembersStore } from "../js/pinia-stores/members.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { uploadImageToR2 } from "../js/composables/useR2Image.js";
import ContributionForm from "../components/campaign/ContributionForm.vue";
import ContributionSettingsEditor from "../components/campaign/ContributionSettingsEditor.vue";
import ParticipantRegisterForm from "../components/campaign/ParticipantRegisterForm.vue";
import ItemTierCard from "../components/campaign/ItemTierCard.vue";
import CycleTracker from "../components/campaign/CycleTracker.vue";
import BidPanel from "../components/campaign/BidPanel.vue";
import CampaignProgressBar from "../components/campaign/CampaignProgressBar.vue";
import CampaignSetupChecklist from "../components/campaign/CampaignSetupChecklist.vue";
import CampaignLifecycleBar from "../components/campaign/CampaignLifecycleBar.vue";
import PayoutSection from "../components/payout/PayoutSection.vue";
import PayoutKycWarning from "../components/payout/PayoutKycWarning.vue";
import { useTransactAuth } from "../js/composables/useTransactAuth.js";
import i18n from "../js/i18n.js";
const { t } = i18n.global;

const props = defineProps({
  groupId: { type: String, required: true },
  campaignId: { type: String, required: true },
  f7route: Object,
  f7router: Object,
});

const { checkAuth } = useTransactAuth({
  f7route: props.f7route,
  f7router: props.f7router,
});

const campaignsStore = useCampaignsStore();
const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();

const campaign = ref(null);
const items = ref([]);
const imageInput = ref(null);
const pendingField = ref("");
const launching = ref(false);

async function launchCampaign() {
  f7.preloader.show();
  launching.value = true;
  try {
    await campaignsStore.activate(props.campaignId);
    f7.toast.show({ text: t("common.active"), closeTimeout: 1500 });
    await reload();
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || t("common.updateFailed"));
  } finally {
    launching.value = false;
    f7.preloader.hide();
  }
}

function openTontine() {
  f7router.navigate(`/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/`);
}

const initial = computed(
  () => campaign.value?.name?.trim()?.charAt(0)?.toUpperCase() || "C",
);

const canManage = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

const adminCount = computed(
  () =>
    membersStore.members.filter((m) => m.role === "OWNER" || m.role === "ADMIN")
      .length,
);

const {
  supportsContributions,
  supportsParticipants,
  supportsItems,
  supportsCycles,
  supportsBids,
  supportsPayouts,
  isClosed,
} = useCampaign(campaign);

const statusLabels = {
  DRAFT: t("campaign.lifecycle.DRAFT"),
  SETUP_REQUIRED: t("campaign.lifecycle.SETUP_REQUIRED"),
  READY_TO_PUBLISH: t("campaign.lifecycle.READY_TO_PUBLISH"),
  PENDING_VERIFICATION: t("campaign.lifecycle.PENDING_VERIFICATION"),
  ACTIVE: t("campaign.lifecycle.ACTIVE"),
  PAUSED: t("campaign.lifecycle.PAUSED"),
  COMPLETED: t("campaign.lifecycle.COMPLETED"),
  FAILED: t("campaign.lifecycle.FAILED"),
  CANCELLED: t("campaign.lifecycle.CANCELLED"),
  EXPIRED: t("campaign.lifecycle.EXPIRED"),
  ARCHIVED: t("campaign.lifecycle.ARCHIVED"),
};
function statusLabel(s) {
  return statusLabels[s] || t("common.unknownStatus", { status: s }) || s;
}

async function load() {
  const { data } = await client.get(`/campaigns/${props.campaignId}`);
  campaign.value = data;
  if (supportsItems.value) {
    const res = await client.get(`/campaigns/${props.campaignId}/items`);
    items.value = res.data;
  }
}

async function reload() {
  await load();
}

function onSettingsSaved() {
  reload();
  f7.toast.show({ text: t("category.settingsUpdated"), closeTimeout: 1500 });
}

const categoryAmounts = computed(() => {
  const raw = campaign.value?.config?.categoryAmounts;
  if (!raw) return [];
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
});

const lockCategoryAmounts = computed(() => {
  const v = campaign.value?.config?.categoryAmountLocked;
  return v == null || String(v) !== "false";
});

const freeContributionText = computed(
  () => campaign.value?.config?.freeContributionText || "",
);

function fmtAmount(n) {
  return `${Number(n || 0).toLocaleString("fr-FR")} CFA`;
}

function categoryDisplay(ca) {
  const meta = categoryMeta(ca.category)
  const typeLabel = meta ? t(meta.labelKey) : ca.category
  if (ca.category === 'CUSTOM') return ca.name || ca.label || typeLabel
  return ca.label ? `${typeLabel} · ${ca.label}` : typeLabel
}

function pickImage(field) {
  pendingField.value = field;
  imageInput.value?.click();
}

async function onImagePick(event) {
  const file = event.target.files?.[0];
  event.target.value = null;
  const field = pendingField.value;
  if (!file || !field) return;
  if (!file.type.startsWith("image/")) {
    f7.toast.show({ text: t("common.pleaseChooseImage"), closeTimeout: 2000 });
    return;
  }
  f7.preloader.show();
  try {
    const url = await uploadImageToR2(file);
    if (!url) {
      f7.dialog.alert(t("common.unableToUpdateImage"));
      return;
    }
    await campaignsStore.updateCampaign(props.campaignId, { [field]: url });
    if (campaign.value) campaign.value[field] = url;
    f7.toast.show({
      text:
        field === "banner"
          ? t("common.bannerUpdated")
          : t("common.logoUpdated"),
      closeTimeout: 1500,
    });
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || t("common.updateFailed"));
  } finally {
    f7.preloader.hide();
  }
}

async function loadMembers() {
  try {
    await membersStore.fetchMembers(props.groupId);
  } catch (e) {
    // members data unavailable — edit affordance hidden
  }
}

load();
loadMembers();
</script>

<style scoped>
.loading-block {
  text-align: center;
  padding: 40px 0;
}
.campaign-hero {
  background: var(--avocash-surface-container-low, #edf6ed);
  border-radius: 16px;
  overflow: hidden;
  margin: 16px;
  border: 1px solid rgba(111, 122, 113, 0.12);
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06);
}
.banner-wrap {
  position: relative;
  height: 140px;
  background: var(--avocash-surface-container, #e8f0e7);
}
.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1A6B42;
}
.hero-body {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: var(--f7-card-bg-color, #fff);
}
.logo-wrap {
  position: relative;
  flex-shrink: 0;
}
.campaign-logo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 3px solid #fff;
  background: var(--f7-card-bg-color, #fff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.logo-placeholder {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  background: #1A6B42;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.hero-text {
  min-width: 0;
}
.hero-name {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 18px;
  font-weight: 700;
  color: #161d18;
}
.edit-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(4px);
}
.banner-edit {
  right: 10px;
  bottom: 10px;
  padding: 6px 12px;
  border-radius: 9999px;
}
.logo-edit {
  right: 0;
  bottom: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  justify-content: center;
  border: 2px solid #fff;
}
.type-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--avocash-font-body, 'Work Sans', sans-serif);
}
.type-name {
  font-family: var(--avocash-font-headline, 'Manrope', sans-serif);
  font-size: 16px;
  font-weight: 600;
  color: #161d18;
}
.type-status {
  font-family: var(--avocash-font-body, 'Work Sans', sans-serif);
  font-size: 11px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.type-status.draft {
  color: var(--f7-on-surface-variant, #8e8e93);
}
.type-status.setup_required {
  color: #a08520;
}
.type-status.ready_to_publish {
  color: #0e7490;
}
.type-status.pending_verification {
  color: #a08520;
}
.type-status.active {
  color: #1a6b42;
}
.type-status.paused {
  color: #a08520;
}
.type-status.completed {
  color: #185fa5;
}
.type-status.failed,
.type-status.cancelled,
.type-status.expired {
  color: #772e37;
}
.type-status.archived {
  color: var(--f7-on-surface-variant, #8e8e93);
}
.description-text {
  font-family: var(--avocash-font-body, 'Work Sans', sans-serif);
  font-size: 14px;
  color: #3f4941;
  line-height: 1.5;
}
.closed-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #edf6ed;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 13px;
  color: #161d18;
}

.tontine-entry-note {
  font-size: 12px;
  margin: 8px 0 0;
}

.draft-banner {
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 30%, transparent);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.draft-banner-text {
  display: flex;
  align-items: center;
  gap: 10px;
}
.draft-banner-text .f7-icon {
  color: var(--f7-theme-color);
  flex-shrink: 0;
}
.draft-banner-title {
  font-size: 15px;
  font-weight: 600;
}
.draft-banner-sub {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 2px;
}
.draft-banner-btn {
  align-self: stretch;
}
</style>
